import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useBoms, useDeleteBom } from '@/api/boms'
import type { Bom } from '@/api/boms'
import { useModels } from '@/api/models'
import { usePools } from '@/api/pools'
import { useProduct } from '@/api/products'
import { useWorkspace } from '@/api/workspace'
import { BomForm } from '@/components/boms/bom-form'
import { BomHeading, BomLines } from '@/components/boms/bom-lines'
import { bomErrorMessage } from '@/components/boms/error-message'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { formatBusinessDate } from '@/components/opening-balances/format'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'

// BOM editor & history (UI-SCREENS P-08, #103): the current BOM, the versions not yet in effect
// and the history by effective date. The Owner adds a version (today or later; a Product's first
// from the Adoption date, G29 / ADR 0008) and edits or deletes one not yet in effect (#98 Q12).
// Staff see it read only; the API refuses them too.
export const Route = createFileRoute('/w/$slug/products/$productId/bom')({
  component: BomEditor,
  staticData: { breadcrumb: 'boms.title' },
})

// Today in the Workspace timezone, as the server counts it.
function todayIn(timezone: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' })
    .format(new Date())
}

function BomEditor() {
  const { t } = useTranslation()
  const { slug, productId } = Route.useParams()
  const workspace = useWorkspace(slug)
  const product = useProduct(slug, Number(productId))
  const boms = useBoms(slug, Number(productId))
  const models = useModels(slug, Number(productId))
  // Archived ones too, so an archived Pool already on a line shows labelled (#103).
  const pools = usePools(slug, { archived: true })
  // null: nothing open; 'new': adding a version; a number: editing that version.
  const [editing, setEditing] = useState<'new' | number | null>(null)
  const [notice, setNotice] = useState('')

  const queries = [workspace, product, boms, models, pools]
  if (queries.some((query) => query.isPending)) return <LoadingSkeleton variant="page" />
  if (!workspace.data || !product.data || !boms.data || !models.data || !pools.data) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {t('errors.generic')}
      </p>
    )
  }

  const owner = workspace.data.role === 'owner'
  const today = todayIn(workspace.data.timezone)
  const versions = boms.data
  const current = versions.find((bom) => bom.status === 'current')
  // Soonest first: the next one to take effect leads.
  const upcoming = versions.filter((bom) => bom.status === 'future').reverse()
  const past = versions.filter((bom) => bom.status === 'past')

  function form(bom?: Bom) {
    return (
      <BomForm
        slug={slug}
        productId={Number(productId)}
        versions={versions}
        bom={bom}
        startFrom={bom ? undefined : versions[0]}
        models={models.data ?? []}
        pools={pools.data ?? []}
        today={today}
        adoptedOn={workspace.data?.adopted_on ?? null}
        onCancel={() => setEditing(null)}
        onSaved={() => {
          setEditing(null)
          setNotice(t('boms.saved'))
        }}
      />
    )
  }

  function version(bom: Bom) {
    return (
      <Card key={bom.id}>
        <CardContent className="flex flex-col gap-3 py-4">
          <BomHeading bom={bom} />
          {editing === bom.id ? (
            form(bom)
          ) : (
            <>
              <BomLines bom={bom} />
              <VersionActions slug={slug} bom={bom} onEdit={() => setEditing(bom.id)} onNotice={setNotice} />
            </>
          )}
        </CardContent>
      </Card>
    )
  }

  return (
    <section className="flex max-w-3xl flex-col gap-6">
      <Link
        to="/w/$slug/products/$productId"
        params={{ slug, productId }}
        className="text-sm underline-offset-4 hover:underline"
      >
        {t('boms.back')}
      </Link>
      <div className="flex flex-col gap-1">
        <h1 className="font-heading text-xl font-semibold">{t('boms.title')}</h1>
        <p className="text-sm text-muted-foreground">
          {product.data.code} · {product.data.name}
        </p>
      </div>
      <p role="status" aria-live="polite" className="text-sm empty:hidden">
        {notice}
      </p>

      {owner ? (
        editing === 'new' ? (
          <section className="flex flex-col gap-3">
            <h2 className="font-medium">{t('boms.form.newTitle')}</h2>
            {form()}
          </section>
        ) : (
          <Button className="h-11 self-start" onClick={() => setEditing('new')}>
            {t('boms.newVersion')}
          </Button>
        )
      ) : null}

      <section className="flex flex-col gap-3">
        <h2 className="font-medium">{t('boms.current')}</h2>
        {current ? (
          version(current)
        ) : (
          <p className="text-sm text-muted-foreground">
            {versions.length === 0 ? t('products.detail.empty.bom') : t('boms.noCurrent')}
          </p>
        )}
      </section>

      {upcoming.length > 0 ? (
        <section className="flex flex-col gap-3">
          <h2 className="font-medium">{t('boms.upcoming')}</h2>
          {upcoming.map(version)}
        </section>
      ) : null}

      {past.length > 0 ? (
        <section className="flex flex-col gap-3">
          <h2 className="font-medium">{t('boms.history')}</h2>
          {past.map(version)}
        </section>
      ) : null}
    </section>
  )
}

function VersionActions({
  slug,
  bom,
  onEdit,
  onNotice,
}: {
  slug: string
  bom: Bom
  onEdit: () => void
  onNotice: (notice: string) => void
}) {
  const { t } = useTranslation()
  const deleteBom = useDeleteBom()
  const [confirming, setConfirming] = useState(false)

  if (!bom.can.update && !bom.can.destroy) return null

  function remove() {
    setConfirming(false)
    deleteBom.mutate({ params: { path: { slug, id: bom.id } } }, { onSuccess: () => onNotice(t('boms.delete.deleted')) })
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-2">
        {bom.can.update ? (
          <Button variant="outline" className="h-11" onClick={onEdit}>
            {t('boms.edit')}
          </Button>
        ) : null}
        {bom.can.destroy ? (
          <Button
            variant="ghost"
            className="h-11 text-destructive"
            disabled={deleteBom.isPending}
            onClick={() => setConfirming(true)}
          >
            {t('boms.delete.delete')}
          </Button>
        ) : null}
      </div>
      <p role="alert" aria-live="assertive" className="text-sm text-destructive empty:hidden">
        {deleteBom.isError ? bomErrorMessage(t, deleteBom.error) : ''}
      </p>

      {confirming ? (
        <AlertDialog open onOpenChange={(open) => (open ? undefined : setConfirming(false))}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t('boms.delete.confirmTitle', { date: formatBusinessDate(bom.effective_from) })}
              </AlertDialogTitle>
              <AlertDialogDescription>{t('boms.delete.confirmBody')}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="h-11">{t('boms.delete.cancel')}</AlertDialogCancel>
              <AlertDialogAction className="h-11" onClick={remove}>
                {t('boms.delete.confirm')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      ) : null}
    </div>
  )
}
